
import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import { cva, type VariantProps } from "class-variance-authority"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { useInsideSheet } from "@/contexts/SheetLayerContext"
import { useForceSheetMobile } from "@/hooks/use-mobile"

const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal

const sheetVariants = cva(
  "fixed gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4  border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  }
)

const RESIZE_HANDLE_CLASSES: Record<string, string> = {
  right: "left-0 top-0 h-full w-2 cursor-col-resize -translate-x-1/2",
  left: "right-0 top-0 h-full w-2 cursor-col-resize translate-x-1/2",
  top: "bottom-0 left-0 w-full h-2 cursor-row-resize translate-y-1/2",
  bottom: "top-0 left-0 w-full h-2 cursor-row-resize -translate-y-1/2",
}

function useIsDesktop(breakpoint = 640): boolean {
  const [isDesktop, setIsDesktop] = React.useState(
    typeof window !== "undefined" ? window.innerWidth >= breakpoint : false
  )

  React.useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${breakpoint}px)`)
    setIsDesktop(mql.matches)
    const handler = (event: MediaQueryListEvent) => setIsDesktop(event.matches)
    mql.addEventListener("change", handler)
    return () => mql.removeEventListener("change", handler)
  }, [breakpoint])

  return isDesktop
}

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  resizable?: boolean
  minSize?: number
  maxSize?: number
  /** Hide the built-in top-right close button (use when an inline close is rendered inside `children`). */
  hideCloseButton?: boolean
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(
  (
    {
      side = "right",
      className,
      children,
      resizable = true,
      minSize = 280,
      maxSize,
      hideCloseButton = false,
      ...props
    },
    ref
  ) => {
    const [size, setSize] = React.useState<number | undefined>(undefined)
    const contentRef = React.useRef<HTMLDivElement | null>(null)
    const isResizingRef = React.useRef(false)
    const isDesktop = useIsDesktop()
    const forceMobile = useForceSheetMobile()

    const isHorizontal = side === "left" || side === "right"
    const canResize = resizable && isDesktop && !forceMobile

    const handleResizeStart = React.useCallback(
      (event: React.PointerEvent<HTMLDivElement>) => {
        if (!canResize || !contentRef.current) return
        event.preventDefault()

        const contentEl = contentRef.current
        const startSize = isHorizontal
          ? contentEl.offsetWidth
          : contentEl.offsetHeight
        const startPos = isHorizontal ? event.clientX : event.clientY

        const computedMax =
          maxSize ??
          (isHorizontal
            ? window.innerWidth * 0.9
            : window.innerHeight * 0.9)

        isResizingRef.current = true
        document.body.style.cursor = isHorizontal
          ? "col-resize"
          : "row-resize"
        document.body.style.userSelect = "none"

        const handlePointerMove = (moveEvent: PointerEvent) => {
          if (!isResizingRef.current) return
          const currentPos = isHorizontal
            ? moveEvent.clientX
            : moveEvent.clientY

          const delta =
            side === "right" || side === "bottom"
              ? startPos - currentPos
              : currentPos - startPos

          const newSize = Math.min(
            Math.max(startSize + delta, minSize),
            computedMax
          )
          setSize(newSize)
        }

        const handlePointerUp = () => {
          isResizingRef.current = false
          document.body.style.cursor = ""
          document.body.style.userSelect = ""
          document.removeEventListener("pointermove", handlePointerMove)
          document.removeEventListener("pointerup", handlePointerUp)
        }

        document.addEventListener("pointermove", handlePointerMove)
        document.addEventListener("pointerup", handlePointerUp)
      },
      [canResize, isHorizontal, side, minSize, maxSize]
    )

    const handleDoubleClick = React.useCallback(() => {
      setSize(undefined)
    }, [])

    const mergedRef = React.useCallback(
      (node: HTMLDivElement | null) => {
        contentRef.current = node
        if (typeof ref === "function") {
          ref(node)
        } else if (ref) {
          ;(ref as React.MutableRefObject<HTMLDivElement | null>).current =
            node
        }
      },
      [ref]
    )

    const elevated = useInsideSheet();

    const sizeStyle: React.CSSProperties = forceMobile
      ? { width: "100vw", height: "100dvh", maxWidth: "none", maxHeight: "none" }
      : canResize && size !== undefined
        ? isHorizontal
          ? { width: `${size}px`, maxWidth: "none" }
          : { height: `${size}px`, maxHeight: "none" }
        : {}

    return (
      <SheetPortal>
        <SheetOverlay />
        <SheetPrimitive.Content
          ref={mergedRef}
          className={cn(
            sheetVariants({ side }),
            elevated ? "z-[60] pointer-events-auto" : "z-50",
            forceMobile && "inset-0 w-full h-full max-w-none border-none",
            className,
          )}
          style={sizeStyle}
          {...props}
        >
          {canResize && side && (
            <div
              onPointerDown={handleResizeStart}
              onDoubleClick={handleDoubleClick}
              className={cn(
                "absolute z-[51] touch-none select-none",
                "transition-colors hover:bg-primary/10 active:bg-primary/20",
                RESIZE_HANDLE_CLASSES[side]
              )}
            />
          )}
          {children}
          {!hideCloseButton && (
            <SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </SheetPrimitive.Close>
          )}
        </SheetPrimitive.Content>
      </SheetPortal>
    )
  }
)
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  const elevated = useInsideSheet();
  return (
    <SheetPrimitive.Overlay
      className={cn(
        "fixed inset-0 bg-background/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        elevated ? "z-[58] pointer-events-auto" : "z-50",
        className
      )}
      {...props}
      ref={ref}
    />
  );
})
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
SheetHeader.displayName = "SheetHeader"

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
SheetFooter.displayName = "SheetFooter"

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold text-foreground", className)}
    {...props}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
